import { Select, Space, Spin } from 'antd';
import { DefaultOptionType } from 'antd/lib/select';
import debounce from 'lodash/debounce';
import { GitLabApi } from 'osep-generator-service-client-v1';
import React, { useCallback, useMemo, useRef, useState } from 'react';
import { useConnectionsQuery } from '../../hooks/query/useConnectionsQuery';
import { useApiConfig } from '../../hooks/useApiConfig';
import { GitLabGroupVariableModel } from '../../iasConfigSchema';
import ConnectionButton from '../connections/ConnectionButton';
import VariableFormItem from '../variables/VariableFormItem';

type GitLabGroup = {
  id: string;
  name: string;
  fullPath: string;
};

type GroupOption = GitLabGroup & DefaultOptionType;

const encodeGitLabMinAccessLevel = (
  level: GitLabGroupVariableModel['min_access_level'],
) => {
  switch (level) {
    case 'Guest':
      return 10;
    case 'Planner':
      return 15;
    case 'Reporter':
      return 20;
    case 'Developer':
      return 30;
    case 'Maintainer':
      return 40;
    case 'Owner':
      return 50;
    default:
      throw new Error(`Unknown GitLab access level: ${level}`);
  }
};

const GitLabGroupSelect: React.FC<{
  variable: GitLabGroupVariableModel;
  disabled: boolean;
}> = ({ variable, disabled }) => {
  const [options, setOptions] = useState<GroupOption[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const { getApiConfig, axiosInstance } = useApiConfig();
  const { data: connections } = useConnectionsQuery();

  const searchRef = useRef(0);

  const handleSearch = useCallback(
    async (searchTerm: string) => {
      searchRef.current += 1;
      const searchId = searchRef.current;

      const apiConfig = await getApiConfig();
      const gitLabApi = new GitLabApi(apiConfig, undefined, axiosInstance);

      setOptions([]);
      setIsLoading(true);

      try {
        const groupsResponse = await gitLabApi.searchGroups({
          searchTerm,
          minAccessLevel: encodeGitLabMinAccessLevel(variable.min_access_level),
          limit: 10,
        });

        const groups = groupsResponse.data.map((group: any) => ({
          id: group.id,
          name: group.name,
          fullPath: group.fullPath,
        }));

        const groupOptions: GroupOption[] = groups.map(
          (group: GitLabGroup) => ({
            ...group,
            label: `${group.fullPath}`,
            value: variable.property === 'id' ? group.id : group.fullPath,
          }),
        );

        if (searchId !== searchRef.current) {
          return;
        }

        setOptions(groupOptions);
      } catch (error) {
        console.error('Error fetching GitLab groups:', error);
        setOptions([]);
      } finally {
        setIsLoading(false);
      }
    },
    [axiosInstance, getApiConfig, variable.property, variable.min_access_level],
  );

  const handleSearchDebounced = useMemo(
    () => debounce(handleSearch, 300),
    [handleSearch],
  );

  const connectedToGitLab = !connections || !!connections.gitlab;

  return (
    <>
      <VariableFormItem variable={variable}>
        {!connectedToGitLab && (
          <Space style={{ marginBottom: 16 }} direction="vertical" size="large">
            <ConnectionButton provider="gitlab" />
          </Space>
        )}
        <Select
          placeholder="Start typing to search..."
          options={options}
          filterOption={false}
          showSearch={true}
          mode={variable.multiple ? 'multiple' : undefined}
          onSearch={handleSearchDebounced}
          loading={isLoading}
          style={{ width: '100%' }}
          disabled={disabled || !connectedToGitLab}
          optionRender={({ label, data }) => {
            return data.fullPath;
          }}
          notFoundContent={isLoading ? <Spin size="small" /> : undefined}
        />
      </VariableFormItem>
    </>
  );
};

export default GitLabGroupSelect;
